import { Menu, Layout } from "antd";
import { useCallback, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { AppState } from "store";

import {
  batteryIcon1,
  clientsIcon,
  dashboardIcon,
  financialsIcon,
  issuesIcon,
  nextStepIcon,
  settingsIcon,
  usersIcon,
  vehiclesIcon,
  zonesIcon,
  batteryIcon,
  batterySwapIcon,
  batteryFullIcon,
  vehiclesModelIcon,
  notificationCenterIcon,
  tripsIcon
} from "svgs";
import { ROUTES } from "utils/constants";

const { Sider } = Layout;

interface ComponentProps {
  collapsed: boolean;
}

function getIconSpan(icon: JSX.Element) {
  return <span className="icon-span">{icon}</span>;
}

const navList = [
  {
    route: ROUTES.dashboard,
    label: "Dashboard",
    icon: getIconSpan(dashboardIcon),
  },
  { route: ROUTES.users, label: "Users", icon: getIconSpan(usersIcon) },
  {
    route: ROUTES.vehicles,
    label: "Vehicles",
    icon: getIconSpan(vehiclesIcon),
  },
  {
    route: ROUTES.battery,
    label: "Batteries",
    icon: getIconSpan(batteryIcon),
  },
  { route: ROUTES.kiosks, label: "Kiosks", icon: getIconSpan(batterySwapIcon) },
  { route: ROUTES.service_centres, label: "Service Centers", icon: getIconSpan(settingsIcon) },
  { route: ROUTES.zones, label: "Zones", icon: getIconSpan(zonesIcon) },
  { route: ROUTES.issues, label: "Issues", icon: getIconSpan(issuesIcon) },
  { route: ROUTES.clients, label: "Clients", icon: getIconSpan(clientsIcon) },
  { route: ROUTES.notifications, label: "Notifications", icon: getIconSpan(notificationCenterIcon) },
  { route: ROUTES.trips, label: "Trips", icon: getIconSpan(tripsIcon) },
];

function getRoleBasedNavList(userType: number) {

  const roleFilters: Record<number, string[]> = {
    // userType 3,4,5
    3: ["Batteries", "Clients", "Vehicle Models", "Kiosks", "Trips", "Service Centers"],
    4: ["Batteries", "Clients", "Vehicle Models", "Kiosks", "Trips", "Service Centers"],
    5: ["Batteries", "Clients", "Vehicle Models", "Kiosks"],

    // userType 7,8
    7: ["Batteries", "Vehicle Models", "Zones", "Kiosks", "Trips", "Service Centers"],
    8: ["Batteries", "Vehicle Models", "Zones", "Trips", "Service Centers"],

    // userType 6
    6: ["Clients", "Vehicle Models", "Zones", "Trips"],
  };

  const excludedLabels = roleFilters[userType];

  if (!excludedLabels) return navList;

  return navList.filter(
    (navOption) => !excludedLabels.includes(navOption.label)
  );
}


export default function AppNav(props: ComponentProps) {
  const { pathname } = useLocation();
  const { collapsed } = props;
  const [isMobile, setIsMobile] = useState(false);

  // Detect screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const selectedTab = navList.find((tab) => {
    if (tab.route === "/zones") {
      return (
        pathname.includes(tab.route) ||
        pathname.includes("/add-zone") ||
        pathname.includes("/edit-zone")
      );
    }
    if (tab.route === "/vehicles") {
      return (
        pathname.includes(tab.route) ||
        pathname.includes("/vehicle_model")
      );
    }
    return pathname.includes(tab.route);
  });

  const {
    common: { userType = 1 },
  } = useSelector<AppState, AppState>((state) => state);

  const history = useHistory();
  const onNavClick = useCallback(
    ({ key }) => {
      const clickedTab = navList.find((tab) => tab.label === key);
      if (clickedTab) {
        history.push(clickedTab.route);
      }
    },
    [history]
  );

  const menuContent = (
    <Menu
      className="nav-items"
      theme="light"
      mode="inline"
      selectedKeys={[selectedTab?.label || "vehicles"]}
      onClick={onNavClick}
    >
      {getRoleBasedNavList(userType).map((item) => (
        <Menu.Item className="__item" key={item.label} icon={item.icon}>
          {item.label}
        </Menu.Item>
      ))}
    </Menu>
  );

  // Hide on mobile - navigation is now in header
  if (isMobile) {
    return null;
  }

  // Desktop/Tablet view - standard sidebar
  return (
    <Sider
      theme="light"
      width={175}
      className="app-nav"
      trigger={null}
      collapsible
      collapsedWidth={66}
      collapsed={collapsed}
      breakpoint="md"
    >
      <div className="logo" />
      {menuContent}
    </Sider>
  );
}